'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import type { HeroBannerSettings } from '@/lib/cms';
import { getBackendUrl, getOrigin } from '@/lib/env';

function getBaseUrl() {
  return getOrigin();
}

function bannerImageSrc(image?: string | null): string | null {
  if (!image) return null;
  const base = getBaseUrl();
  const path = image.startsWith('/') ? image : `/${image}`;
  return `${base}${path}`;
}

function isRemoteBackendImage(src: string): boolean {
  const backend = getBackendUrl();
  return (
    (backend !== '' && src.startsWith(backend)) ||
    src.startsWith(process.env.API_BASE_URL || '') ||
    src.startsWith(process.env.NEXT_PUBLIC_BACKEND_URL || '')
  );
}

export function HeroBanner({ data }: { data: HeroBannerSettings }) {
  const title = data?.title ?? 'Host Amazing Events';
  const subtitle = data?.subtitle ?? 'Build and manage events easily';
  const description = data?.description;
  const image = bannerImageSrc(data?.image ?? undefined);
  const buttonText = data?.button_text ?? 'Create Event';
  const buttonLink = data?.button_link ?? '/register';
  const overlay = data?.overlay !== false;
  const alignment = data?.alignment ?? 'center';

  const alignClass =
    alignment === 'left'
      ? 'text-left items-start'
      : alignment === 'right'
        ? 'text-right items-end'
        : 'text-center items-center';

  return (
    <section className="relative min-h-[70vh] flex flex-col justify-center overflow-hidden rounded-t-3xl sm:rounded-t-[2rem]">
      {/* Background image */}
      {image && (
        <div className="absolute inset-0 z-0">
          <Image
            src={image}
            alt=""
            fill
            className="object-cover object-center"
            sizes="100vw"
            priority
            unoptimized={isRemoteBackendImage(image)}
          />
        </div>
      )}
      {/* Overlay gradient */}
      {overlay && (
        <div
          className="absolute inset-0 z-[1] bg-gradient-to-b from-black/70 via-black/50 to-black/70"
          aria-hidden
        />
      )}
      {/* Content */}
      <div
        className={`relative z-[2] flex flex-col px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28 ${alignClass}`}
      >
        <div className="max-w-4xl mx-auto w-full">
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-[3.5rem] font-bold text-text-inverse tracking-tight leading-tight"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {title}
          </motion.h1>
          <motion.p
            className="mt-4 sm:mt-5 text-lg sm:text-xl lg:text-2xl text-text-inverse/95 font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
          >
            {subtitle}
          </motion.p>
          {description && (
            <motion.p
              className="mt-3 text-base sm:text-lg text-text-inverse/85 max-w-2xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12, ease: 'easeOut' }}
            >
              {description}
            </motion.p>
          )}
          <motion.div
            className={`mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4 ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18, ease: 'easeOut' }}
          >
            <Link
              href={buttonLink}
              className="inline-flex items-center justify-center px-6 py-3.5 sm:px-8 sm:py-4 bg-plaster text-soot font-semibold rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              {buttonText}
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
